/**
 * Sreshta Cart Drawer
 * File: assets/sreshta-cart-drawer.js
 */
(() => {
  'use strict';

  const drawer        = document.getElementById('ScdDrawer');
  const overlay       = document.getElementById('ScdOverlay');
  const closeBtn      = document.getElementById('ScdClose');
  const bodyEl        = document.getElementById('ScdBody');
  const footerEl      = document.getElementById('ScdFooter');
  const countEl       = document.getElementById('ScdCount');
  const subtotalEl    = document.getElementById('ScdSubtotal');
  const discountInput = document.getElementById('ScdDiscountInput');
  const discountApply = document.getElementById('ScdDiscountApply');
  const discountMsg   = document.getElementById('ScdDiscountMsg');

  if (!drawer || !overlay) return;

  // ── STATE ────────────────────────────────────────────────────────────────

  let isOpen   = false;
  let cart     = null;   // cached cart object
  let pending  = false;  // network request in flight

  // ── MONEY ────────────────────────────────────────────────────────────────

  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR';
  const locale   = document.documentElement.lang || 'en-IN';

  function money(cents) {
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 2 }).format(cents / 100);
    } catch (_) {
      return (cents / 100).toFixed(2);
    }
  }

  // ── HELPERS ──────────────────────────────────────────────────────────────

  function esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function sizedImg(url, size) {
    if (!url) return '';
    return url.replace(/(\.[^.?]+)(\?|$)/, `_${size}$1$2`);
  }

  // ── OPEN / CLOSE ─────────────────────────────────────────────────────────

  function openDrawer() {
    if (isOpen) return;
    isOpen = true;
    drawer.classList.add('scd-open');
    overlay.classList.add('scd-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    if (cart) {
      // Render from cache immediately — zero wait
      renderCart(cart);
    } else {
      // First open, no cache yet — show spinner and fetch
      showSpinner();
      fetchCart();
    }
  }

  function closeDrawer() {
    if (!isOpen) return;
    isOpen = false;
    drawer.classList.remove('scd-open');
    overlay.classList.remove('scd-open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  overlay.addEventListener('click', e => { if (e.target === overlay) closeDrawer(); });
  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && isOpen) closeDrawer(); });

  // ── FETCH CART (background, updates cache) ────────────────────────────────

  function showSpinner() {
    if (bodyEl) {
      bodyEl.innerHTML = '<div class="scd-loading"><div class="scd-spinner"></div></div>';
      bodyEl.style.cssText = 'display:flex;flex:1;align-items:center;justify-content:center;';
    }
    if (footerEl) footerEl.style.display = 'none';
  }

  function fetchCart(onDone) {
    if (pending) { if (onDone) onDone(); return; }
    pending = true;

    fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        pending = false;
        cart = data;
        if (isOpen) renderCart(data);
        updateBubble(data.item_count);
        if (onDone) onDone();
      })
      .catch(() => {
        pending = false;
        if (isOpen && !cart) renderError();
        if (onDone) onDone();
      });
  }

  // ── RENDER ────────────────────────────────────────────────────────────────

  function renderCart(data) {
    if (!bodyEl) return;

    if (!data.items || data.items.length === 0) {
      renderEmpty();
      return;
    }

    if (countEl) {
      const n = data.item_count;
      countEl.textContent = `${n} ${n === 1 ? 'item' : 'items'}`;
    }

    bodyEl.style.cssText = 'display:block;flex:1;overflow-y:auto;overflow-x:hidden;';
    bodyEl.innerHTML = `<ul class="scd-items" id="ScdItems">${data.items.map(itemHTML).join('')}</ul>`;

    if (footerEl) {
      footerEl.style.display = '';
      if (subtotalEl) subtotalEl.textContent = money(data.total_price);
    }

    bindEvents();
  }

  function renderEmpty() {
    if (bodyEl) {
      bodyEl.style.cssText = 'display:flex;flex:1;align-items:center;justify-content:center;';
      bodyEl.innerHTML = `
        <div class="scd-empty">
          <span class="scd-empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 01-8 0"/>
            </svg>
          </span>
          <p class="scd-empty-title">Your cart is empty.</p>
          <p class="scd-empty-sub">Add a formulation to begin.</p>
          <a href="/collections/all" class="scd-continue">Browse catalog</a>
        </div>`;
      bodyEl.querySelector('.scd-continue')?.addEventListener('click', closeDrawer);
    }
    if (footerEl) footerEl.style.display = 'none';
    if (countEl) countEl.textContent = '';
  }

  function renderError() {
    if (bodyEl) {
      bodyEl.style.cssText = 'display:flex;flex:1;align-items:center;justify-content:center;';
      bodyEl.innerHTML = `<div class="scd-empty"><p class="scd-empty-title">Could not load cart.</p><p class="scd-empty-sub">Please refresh the page.</p></div>`;
    }
  }

  function itemHTML(item) {
    const img = item.featured_image && item.featured_image.url
      ? `<img src="${esc(sizedImg(item.featured_image.url, '160x160'))}" alt="${esc(item.title)}" width="80" height="80" loading="eager">`
      : `<div class="scd-item-image-placeholder">—</div>`;

    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<span class="scd-item-meta">${esc(item.variant_title)}</span>` : '';

    let props = '';
    if (item.properties) {
      for (const [k, v] of Object.entries(item.properties)) {
        if (k && !k.startsWith('_') && v) props += `<span class="scd-item-meta scd-item-meta--gold">${esc(k)}: ${esc(String(v))}</span>`;
      }
    }

    const compare = item.original_price > item.final_price
      ? `<s class="scd-item-compare">${money(item.original_price)}</s>` : '';

    return `
      <li class="scd-item" data-key="${esc(item.key)}">
        <div class="scd-item-image">${img}</div>
        <div class="scd-item-info">
          <div class="scd-item-row scd-item-row--top">
            <a href="${esc(item.url)}" class="scd-item-title">${esc(item.product_title || item.title)}</a>
            <button class="scd-item-remove" data-key="${esc(item.key)}" aria-label="Remove">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="11" height="11"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>
          ${variant}${props}
          <div class="scd-item-row scd-item-row--bottom">
            <div class="scd-qty">
              <button class="scd-qty-btn" data-action="minus" data-key="${esc(item.key)}" aria-label="Decrease">−</button>
              <span class="scd-qty-num" data-key="${esc(item.key)}">${item.quantity}</span>
              <button class="scd-qty-btn" data-action="plus" data-key="${esc(item.key)}" aria-label="Increase">+</button>
            </div>
            <span class="scd-item-price">${money(item.final_line_price)}${compare}</span>
          </div>
        </div>
      </li>`;
  }

  // ── BIND EVENTS ──────────────────────────────────────────────────────────

  function bindEvents() {
    const list = document.getElementById('ScdItems');
    if (!list) return;

    // Single delegated listener for the whole list
    list.addEventListener('click', e => {
      // Remove button
      const removeBtn = e.target.closest('.scd-item-remove');
      if (removeBtn) {
        e.preventDefault();
        removeItem(removeBtn.dataset.key);
        return;
      }

      // Qty minus
      const qtyBtn = e.target.closest('.scd-qty-btn');
      if (qtyBtn) {
        e.preventDefault();
        const key     = qtyBtn.dataset.key;
        const numEl   = list.querySelector(`.scd-qty-num[data-key="${key}"]`);
        const current = numEl ? parseInt(numEl.textContent, 10) : 1;
        const action  = qtyBtn.dataset.action;
        const newQty  = action === 'minus' ? Math.max(0, current - 1) : Math.min(99, current + 1);
        changeQty(key, newQty, numEl);
        return;
      }

      // Title link
      const titleLink = e.target.closest('.scd-item-title');
      if (titleLink) closeDrawer();
    });
  }

  // ── OPTIMISTIC REMOVE ─────────────────────────────────────────────────────

  function removeItem(key) {
    const itemEl = document.querySelector(`.scd-item[data-key="${key}"]`);
    if (!itemEl) return;

    // 1. Instantly hide the element
    itemEl.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
    itemEl.style.opacity = '0';
    itemEl.style.transform = 'translateX(16px)';

    // 2. Update local cache immediately
    if (cart) {
      cart.items = cart.items.filter(i => i.key !== key);
      // Recalculate total from remaining items
      cart.item_count = cart.items.reduce((sum, i) => sum + i.quantity, 0);
      cart.total_price = cart.items.reduce((sum, i) => sum + i.final_line_price, 0);
      updateBubble(cart.item_count);
      if (subtotalEl) subtotalEl.textContent = money(cart.total_price);
    }

    // 3. Remove from DOM after animation
    setTimeout(() => {
      itemEl.remove();
      // If list is now empty, show empty state
      const list = document.getElementById('ScdItems');
      if (list && list.children.length === 0) renderEmpty();
    }, 200);

    // 4. Sync with server in background (no UI wait)
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: 0 }),
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        // Update cache with server truth
        cart = data;
        updateBubble(data.item_count);
        // Only re-render if still open and data differs significantly
        if (isOpen && subtotalEl) subtotalEl.textContent = money(data.total_price);
      })
      .catch(() => {
        // Re-fetch on error to re-sync
        fetchCart();
      });
  }

  // ── OPTIMISTIC QTY CHANGE ─────────────────────────────────────────────────

  function changeQty(key, newQty, numEl) {
    if (newQty === 0) {
      removeItem(key);
      return;
    }

    // Instantly update display
    if (numEl) numEl.textContent = newQty;

    // Update local cache
    if (cart) {
      const item = cart.items.find(i => i.key === key);
      if (item) {
        item.quantity        = newQty;
        item.final_line_price = item.final_price * newQty;
        cart.item_count      = cart.items.reduce((s, i) => s + i.quantity, 0);
        cart.total_price     = cart.items.reduce((s, i) => s + i.final_line_price, 0);
        if (subtotalEl) subtotalEl.textContent = money(cart.total_price);
        if (countEl) countEl.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
        updateBubble(cart.item_count);

        // Update line price display
        const itemEl    = document.querySelector(`.scd-item[data-key="${key}"]`);
        const priceEl   = itemEl ? itemEl.querySelector('.scd-item-price') : null;
        if (priceEl) {
          const compare = item.original_price > item.final_price
            ? `<s class="scd-item-compare">${money(item.original_price)}</s>` : '';
          priceEl.innerHTML = money(item.final_line_price) + compare;
        }
      }
    }

    // Sync with server in background
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: newQty }),
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        cart = data;
        updateBubble(data.item_count);
        if (isOpen && subtotalEl) subtotalEl.textContent = money(data.total_price);
      })
      .catch(() => fetchCart());
  }

  // ── DISCOUNT ──────────────────────────────────────────────────────────────

  if (discountApply && discountInput) {
    discountApply.addEventListener('click', applyDiscount);
    discountInput.addEventListener('keydown', e => { if (e.key === 'Enter') applyDiscount(); });
  }

  function applyDiscount() {
    const code = (discountInput.value || '').trim().toUpperCase();
    if (!code) return;
    discountApply.disabled = true;
    discountApply.textContent = '···';

    const checkoutBtn = document.getElementById('ScdCheckout');
    if (checkoutBtn) checkoutBtn.href = `/checkout?discount=${encodeURIComponent(code)}`;

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: { discount_code: code } }),
    })
      .finally(() => {
        discountApply.disabled = false;
        discountApply.textContent = 'Apply';
        showDiscountMsg(`"${code}" will be applied at checkout.`, 'success');
      });
  }

  function showDiscountMsg(msg, type) {
    if (!discountMsg) return;
    discountMsg.textContent = msg;
    discountMsg.className   = `scd-discount-msg scd-${type}`;
    setTimeout(() => { if (discountMsg) discountMsg.className = 'scd-discount-msg'; }, 4000);
  }

  // ── HEADER BUBBLE ─────────────────────────────────────────────────────────

  function updateBubble(count) {
    // Refresh via Shopify sections API
    fetch('/?sections=cart-icon-bubble', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(r => r.json())
      .then(data => {
        if (data && data['cart-icon-bubble']) {
          const doc    = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html');
          const newEl  = doc.getElementById('cart-icon-bubble');
          const curEl  = document.getElementById('cart-icon-bubble');
          if (newEl && curEl) curEl.innerHTML = newEl.innerHTML;
        }
      })
      .catch(() => {
        // Fallback: manually update any visible count elements
        document.querySelectorAll('.cart-count-bubble').forEach(el => {
          const span = el.querySelector('[aria-hidden]') || el;
          if (span) span.textContent = count > 0 ? count : '';
          el.style.display = count > 0 ? '' : 'none';
        });
      });
  }

  // ── CART ICON INTERCEPT ───────────────────────────────────────────────────

  function interceptCartLinks() {
    document.querySelectorAll('a[href="/cart"], a[href$="/cart"]').forEach(link => {
      if (link.closest('#ScdDrawer')) return;
      if (link._scdBound) return;
      link._scdBound = true;
      link.addEventListener('click', e => { e.preventDefault(); openDrawer(); });
    });
  }

  // ── ATC INTERCEPT ────────────────────────────────────────────────────────

  const _fetch = window.fetch;
  window.fetch = function(...args) {
    const url = (typeof args[0] === 'string' ? args[0] : args[0]?.url) || '';
    if (!url.includes('/cart/add')) return _fetch.apply(this, args);

    return _fetch.apply(this, args).then(response => {
      if (response.ok) {
        // Fetch updated cart first, then open — so data is ready when drawer opens
        fetch('/cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
          .then(r => r.json())
          .then(data => {
            cart = data;
            updateBubble(data.item_count);
            openDrawer();
          })
          .catch(() => openDrawer());
      }
      return response;
    });
  };

  // ── SUPPRESS DAWN NOTIFICATION ────────────────────────────────────────────

  function patchDawn() {
    const el = document.querySelector('cart-notification');
    if (!el || el._scdPatched) return;
    el._scdPatched = true;
    el.open            = () => {};
    el.renderContents  = () => {};
  }

  new MutationObserver(patchDawn).observe(document.body, { childList: true, subtree: false });
  if (window.customElements?.whenDefined) {
    window.customElements.whenDefined('cart-notification').then(patchDawn).catch(() => {});
  }

  // ── INIT ──────────────────────────────────────────────────────────────────

  function init() {
    patchDawn();
    interceptCartLinks();
    setTimeout(interceptCartLinks, 800);
    // Pre-fetch cart eagerly so first open is instant
    fetchCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.SreshtaCart = { open: openDrawer, close: closeDrawer, refresh: fetchCart };

})();
